import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckSquare,
  ClipboardCheck,
  Clock,
  FileText,
  Mail,
  Phone,
  Target,
} from 'lucide-react';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import apiService from '../../services/api';
import {
  NR2J_ACTIVE_PIPELINE_STAGES,
  effectiveStatus,
  formatCentsAsEuros,
  formatNr2jDate,
  nr2jQuoteStatusBadge,
  nr2jStageLabel,
  nr2jStatusBadge,
  nr2jTaskStatusLabel,
  nr2jTaskTypeLabel,
  organizationDisplayName,
} from '../../utils/nr2jConstants';
import {
  isTaskOverdue,
  memberDisplayName,
  memberInitials,
  memberStatutBadge,
  myTeamLabels,
} from '../../utils/nr2jMyTeam.js';
import { readAdminSessionUser } from '../../utils/settingsSessionUser.js';

const TABS = [
  { id: 'overview', label: 'Vue d’ensemble' },
  { id: 'prospects', label: 'Prospects' },
  { id: 'clients', label: 'Clients' },
  { id: 'tasks', label: 'Tâches' },
  { id: 'quotes', label: 'Devis' },
  { id: 'activity', label: 'Activité' },
];

const ALERT_TONES = {
  high: 'border-rose-200 bg-rose-50 text-rose-900',
  medium: 'border-amber-200 bg-amber-50 text-amber-900',
  low: 'border-slate-200 bg-slate-50 text-slate-800',
};

function KpiTile({ title, value, icon: Icon, tone = 'slate', onClick }) {
  const tones = {
    slate: 'border-slate-200 bg-white',
    amber: 'border-amber-200 bg-amber-50',
    rose: 'border-rose-200 bg-rose-50',
    emerald: 'border-emerald-200 bg-emerald-50',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-xl border p-4 text-left shadow-sm transition hover:shadow-md ${tones[tone] || tones.slate}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="mt-1 text-3xl font-semibold text-gray-900">{value ?? 0}</p>
        </div>
        {Icon ? <Icon className="h-6 w-6 text-gray-500" /> : null}
      </div>
    </button>
  );
}

function Section({ title, action, children, testId }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{title}</h2>
        {action}
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function QuoteBadge({ quote }) {
  const v = quote.latestVersion || quote.currentVersion;
  if (!v) return <span className="text-xs text-gray-400">—</span>;
  const badge = nr2jQuoteStatusBadge(v.effectiveStatus || effectiveStatus(v));
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${badge.className}`}>
      {badge.label}
    </span>
  );
}

function OrgLink({ org, id }) {
  const orgId = id || org?.id;
  if (!orgId) return <span className="text-gray-500">{organizationDisplayName(org)}</span>;
  return (
    <Link
      to={`/nr2j/clients/${orgId}`}
      onClick={(e) => e.stopPropagation()}
      className="font-medium text-indigo-700 hover:underline"
    >
      {organizationDisplayName(org)}
    </Link>
  );
}

function EmptyRow({ text }) {
  return <p className="text-sm text-gray-500">{text}</p>;
}

function OrganizationsTable({ items, emptyText }) {
  const navigate = useNavigate();
  if (!items.length) return <EmptyRow text={emptyText} />;
  return (
    <div className="min-w-0 overflow-hidden">
      <table className="w-full table-fixed divide-y divide-gray-100 text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
          <tr>
            <th className="w-[28%] px-3 py-3">Société</th>
            <th className="hidden w-[12%] px-3 py-3 sm:table-cell">Ville</th>
            <th className="w-[14%] px-3 py-3">Étape</th>
            <th className="hidden w-[18%] px-3 py-3 lg:table-cell">Prochaine action</th>
            <th className="w-[14%] px-3 py-3">Relance</th>
            <th className="hidden w-[12%] px-3 py-3 xl:table-cell">Montant estimé</th>
            <th className="w-[12%] px-3 py-3">Statut</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {items.map((org) => {
            const badge = nr2jStatusBadge(org.commercialStatus);
            const followUpLate = org.nextFollowUpAt && new Date(org.nextFollowUpAt).getTime() <= Date.now();
            return (
              <tr
                key={org.id}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/nr2j/clients/${org.id}`)}
              >
                <td className="min-w-0 truncate px-3 py-3"><OrgLink org={org} /></td>
                <td className="hidden truncate px-3 py-3 text-gray-600 sm:table-cell">{org.ville || '—'}</td>
                <td className="truncate px-3 py-3 text-gray-600">{nr2jStageLabel(org.pipelineStage)}</td>
                <td className="hidden truncate px-3 py-3 text-gray-600 lg:table-cell">{org.nextAction || '—'}</td>
                <td className={`px-3 py-3 ${followUpLate ? 'text-rose-700' : 'text-gray-600'}`}>
                  {formatNr2jDate(org.nextFollowUpAt)}
                </td>
                <td className="hidden px-3 py-3 text-gray-600 xl:table-cell">
                  {org.estimatedAmountCents != null ? `${formatCentsAsEuros(org.estimatedAmountCents)} €` : '—'}
                </td>
                <td className="px-3 py-3">
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${badge.className}`}>
                    {badge.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TasksList({ items, emptyText }) {
  if (!items.length) return <EmptyRow text={emptyText} />;
  return (
    <ul className="divide-y divide-gray-100">
      {items.map((task) => {
        const overdue = isTaskOverdue(task);
        return (
          <li key={task.id} className="flex items-start justify-between gap-3 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">{task.title}</p>
              <p className="text-xs text-gray-500">
                {nr2jTaskTypeLabel(task.type)} · {nr2jTaskStatusLabel(task.status)}
              </p>
              <p className={`mt-1 inline-flex items-center gap-1 text-xs ${overdue ? 'text-rose-700' : 'text-gray-500'}`}>
                <Clock className="h-3.5 w-3.5" />
                {formatNr2jDate(task.dueAt)}
                {overdue ? ' · en retard' : ''}
              </p>
            </div>
            <div className="shrink-0 text-right text-xs">
              <OrgLink org={task.organization} id={task.organizationId} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function QuotesTable({ items, emptyText }) {
  const navigate = useNavigate();
  if (!items.length) return <EmptyRow text={emptyText} />;
  return (
    <div className="min-w-0 overflow-hidden">
      <table className="w-full table-fixed divide-y divide-gray-100 text-sm">
        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
          <tr>
            <th className="w-[22%] px-3 py-3">Numéro</th>
            <th className="w-[30%] px-3 py-3">Organisation</th>
            <th className="w-[16%] px-3 py-3">Montant TTC</th>
            <th className="w-[16%] px-3 py-3">Statut</th>
            <th className="hidden w-[16%] px-3 py-3 md:table-cell">Mis à jour</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {items.map((quote) => {
            const v = quote.latestVersion || quote.currentVersion;
            return (
              <tr
                key={quote.id}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => navigate(`/nr2j/devis/${quote.id}`)}
              >
                <td className="min-w-0 truncate px-3 py-3">
                  <Link
                    to={`/nr2j/devis/${quote.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="font-medium text-indigo-700 hover:underline"
                  >
                    {quote.number || 'Brouillon'}
                  </Link>
                  {v?.version ? <span className="ml-1 text-xs text-gray-400">v{v.version}</span> : null}
                </td>
                <td className="min-w-0 truncate px-3 py-3">
                  <OrgLink org={quote.organization} id={quote.organizationId} />
                </td>
                <td className="whitespace-nowrap px-3 py-3 tabular-nums text-gray-600">
                  {v ? `${formatCentsAsEuros(v.totalTtcCents)} €` : '—'}
                </td>
                <td className="px-3 py-3"><QuoteBadge quote={quote} /></td>
                <td className="hidden px-3 py-3 text-xs text-gray-500 md:table-cell">
                  {formatNr2jDate(quote.updatedAt)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ActivityList({ items, emptyText, memberId }) {
  if (!items.length) return <EmptyRow text={emptyText} />;
  return (
    <ul className="divide-y divide-gray-100">
      {items.map((ev) => (
        <li key={ev.id} className="flex items-start justify-between gap-3 py-3">
          <div className="min-w-0">
            <p className="text-sm text-gray-900">{ev.message}</p>
            <p className="text-xs text-gray-500">
              {formatNr2jDate(ev.createdAt)}
              {ev.actorUserId && ev.actorUserId !== memberId ? ' · par un autre collaborateur' : ''}
            </p>
          </div>
          {ev.organizationId ? (
            <div className="shrink-0 text-right text-xs">
              <OrgLink org={ev.organization} id={ev.organizationId} />
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export default function Nr2jMyTeamDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toasts, toast, removeToast } = useToast();
  const [session] = useState(() => readAdminSessionUser());
  const labels = myTeamLabels(session?.role === 'SUPER_ADMIN');

  const tabParam = searchParams.get('tab') || 'overview';
  const tab = TABS.some((t) => t.id === tabParam) ? tabParam : 'overview';

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [sections, setSections] = useState({});

  const setTab = useCallback((id) => {
    setSearchParams(id === 'overview' ? {} : { tab: id }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setSections({});
    try {
      const res = await apiService.getNr2jMyTeamMember(userId);
      setDetail(res || null);
    } catch (error) {
      setDetail(null);
      setLoadError(error?.message || 'Impossible de charger la fiche collaborateur');
      toast.error(error?.message || 'Impossible de charger la fiche collaborateur');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab === 'overview' || !detail || sections[tab]) return undefined;
    let cancelled = false;
    apiService.getNr2jMyTeamMemberSection(userId, tab)
      .then((res) => {
        if (!cancelled) setSections((prev) => ({ ...prev, [tab]: res?.items || [] }));
      })
      .catch((error) => {
        if (!cancelled) {
          toast.error(error?.message || 'Impossible de charger cet onglet');
          setSections((prev) => ({ ...prev, [tab]: [] }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tab, detail, userId, sections]);

  const member = detail?.member || null;
  const stats = detail?.stats || {};
  const ownerQuery = `owner=${encodeURIComponent(`user:${userId}`)}`;

  const pipeline = useMemo(() => {
    const counts = new Map((detail?.pipeline || []).map((row) => [row.stage, row.count]));
    return NR2J_ACTIVE_PIPELINE_STAGES.map((stage) => ({ stage, count: counts.get(stage) || 0 }));
  }, [detail]);
  const pipelineMax = Math.max(1, ...pipeline.map((row) => row.count));

  function alertTarget(alert) {
    switch (alert.code) {
      case 'QUOTES_PENDING_APPROVAL':
        return () => navigate('/nr2j/a-valider');
      case 'TASKS_OVERDUE':
        return () => setTab('tasks');
      default:
        return () => setTab('prospects');
    }
  }

  const backLink = (
    <Link
      to="/nr2j/mon-equipe"
      className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-indigo-700"
    >
      <ArrowLeft className="h-4 w-4" /> {labels.backLabel}
    </Link>
  );

  if (loading) {
    return (
      <div className="min-w-0 space-y-6 overflow-x-hidden" data-testid="nr2j-my-team-detail">
        {backLink}
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500">
          Chargement…
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="min-w-0 space-y-6 overflow-x-hidden" data-testid="nr2j-my-team-detail">
        {backLink}
        <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
          <p className="text-sm text-gray-600">{loadError || 'Collaborateur introuvable.'}</p>
        </div>
        {toasts.map((item) => (
          <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
        ))}
      </div>
    );
  }

  const badge = memberStatutBadge(member.statut);
  const alerts = detail.alerts || [];
  const pendingApprovals = detail.pendingApprovals || [];
  const staleProspects = detail.staleProspects || [];
  const recentQuotes = detail.recentQuotes || [];
  const openTasks = detail.tasks || [];
  const recentActivity = detail.recentActivity || [];
  const sectionItems = sections[tab] || [];
  const sectionPending = tab !== 'overview' && !sections[tab];

  const listLinks = {
    prospects: { to: `/prospection?${ownerQuery}`, label: 'Ouvrir dans Prospection' },
    clients: { to: `/nr2j/clients?${ownerQuery}`, label: 'Ouvrir dans Clients NR2J' },
    tasks: { to: `/nr2j/taches?filter=all&${ownerQuery}`, label: 'Ouvrir dans Tâches' },
    quotes: { to: `/nr2j/devis?${ownerQuery}`, label: 'Ouvrir dans Devis' },
  };

  return (
    <div className="min-w-0 space-y-6 overflow-x-hidden" data-testid="nr2j-my-team-detail">
      {backLink}

      <div className="flex flex-wrap items-start justify-between gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-4">
          <span
            className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-lg font-semibold text-indigo-700"
            aria-hidden="true"
          >
            {memberInitials(member)}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{memberDisplayName(member)}</h1>
              <span className="inline-flex rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-800">
                Commercial
              </span>
              <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${badge.className}`}>
                {badge.label}
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-600">
              {member.managerName
                ? `Rattaché à ${member.managerName} (Manager)`
                : 'Sans manager'}
            </p>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
              {member.email ? (
                <a href={`mailto:${member.email}`} className="inline-flex items-center gap-1 hover:text-indigo-700">
                  <Mail className="h-3.5 w-3.5" /> {member.email}
                </a>
              ) : null}
              {member.telephone ? (
                <a href={`tel:${member.telephone}`} className="inline-flex items-center gap-1 hover:text-indigo-700">
                  <Phone className="h-3.5 w-3.5" /> {member.telephone}
                </a>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to={`/nr2j/devis?${ownerQuery}`}
            className="inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
          >
            <FileText className="h-4 w-4" /> Voir les devis
          </Link>
          <Link
            to={`/prospection?${ownerQuery}`}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <Target className="h-4 w-4" /> Voir les prospects
          </Link>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5" data-testid="nr2j-my-team-detail-kpis">
        <KpiTile title="Prospects actifs" value={stats.prospectsActive} icon={Target} onClick={() => setTab('prospects')} />
        <KpiTile title="À relancer" value={stats.prospectsFollowUp} icon={AlertTriangle} tone="amber" onClick={() => setTab('prospects')} />
        <KpiTile title="Tâches en retard" value={stats.tasksOverdue} icon={CheckSquare} tone="rose" onClick={() => setTab('tasks')} />
        <KpiTile title="Devis en cours" value={stats.quotesOpen} icon={FileText} onClick={() => setTab('quotes')} />
        <KpiTile
          title="Devis à valider"
          value={stats.quotesPendingApproval}
          icon={ClipboardCheck}
          tone="amber"
          onClick={() => navigate('/nr2j/a-valider')}
        />
      </div>

      {pendingApprovals.length > 0 ? (
        <div
          className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3"
          data-testid="nr2j-my-team-detail-approval-banner"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="inline-flex items-center gap-2 text-sm font-semibold text-rose-900">
                <ClipboardCheck className="h-4 w-4" />
                {pendingApprovals.length} devis à valider
              </p>
              <p className="mt-1 text-xs text-rose-800">
                Un ou plusieurs devis nécessitent votre validation pour pouvoir être envoyés au client.
              </p>
            </div>
            <Link to="/nr2j/a-valider" className="text-xs font-medium text-rose-900 hover:underline">
              Voir tous les devis en attente →
            </Link>
          </div>
          <ul className="mt-3 space-y-2">
            {pendingApprovals.slice(0, 3).map((row) => (
              <li key={row.id}>
                <Link
                  to={`/nr2j/a-valider?id=${encodeURIComponent(row.id)}`}
                  className="flex items-center justify-between gap-3 rounded-lg border border-rose-100 bg-white px-3 py-2 hover:bg-rose-50/60"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">
                      Devis {row.quote?.number || ''}
                      {row.quote?.latestVersion
                        ? ` · ${formatCentsAsEuros(row.quote.latestVersion.totalTtcCents)} €`
                        : ''}
                    </p>
                    <p className="truncate text-xs text-gray-500">
                      {organizationDisplayName(row.quote?.organization)}
                      {' · '}créé le {formatNr2jDate(row.requestedAt)}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs text-rose-800">
                    En attente de validation
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-1 border-b border-gray-200" role="tablist" data-testid="nr2j-my-team-detail-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              tab === t.id
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' ? (
        <div className="space-y-6">
          <Section title="Alertes" testId="nr2j-my-team-detail-alerts">
            {alerts.length === 0 ? (
              <EmptyRow text="Aucune alerte — portefeuille à jour." />
            ) : (
              <ul className="grid gap-2 md:grid-cols-2">
                {alerts.map((alert) => (
                  <li key={alert.code}>
                    <button
                      type="button"
                      onClick={alertTarget(alert)}
                      className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left hover:shadow-sm ${
                        ALERT_TONES[alert.severity] || ALERT_TONES.low
                      }`}
                    >
                      <span className="text-sm font-medium">{alert.label}</span>
                      <ArrowRight className="h-4 w-4 flex-shrink-0 opacity-60" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <div className="grid gap-6 lg:grid-cols-2">
            <Section title="Pipeline actif">
              <ul className="space-y-2">
                {pipeline.map((row) => (
                  <li key={row.stage} className="flex items-center gap-3">
                    <span className="w-28 shrink-0 text-sm text-gray-700">{nr2jStageLabel(row.stage)}</span>
                    <span className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                      <span
                        className="block h-full rounded-full bg-indigo-500"
                        style={{ width: `${(row.count / pipelineMax) * 100}%` }}
                      />
                    </span>
                    <span className="w-8 text-right text-sm font-semibold text-gray-900">{row.count}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section
              title="Devis en attente de validation"
              action={(
                <Link to="/nr2j/a-valider" className="text-xs font-medium text-indigo-700">
                  À valider
                </Link>
              )}
            >
              {pendingApprovals.length === 0 ? (
                <EmptyRow text="Aucun devis en attente." />
              ) : (
                <ul className="space-y-2">
                  {pendingApprovals.map((row) => (
                    <li key={row.id}>
                      <Link
                        to={`/nr2j/a-valider?id=${encodeURIComponent(row.id)}`}
                        className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 hover:bg-gray-50"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">
                            Devis {row.quote?.number || ''}
                            {row.quote?.latestVersion
                              ? ` · ${formatCentsAsEuros(row.quote.latestVersion.totalTtcCents)} €`
                              : ''}
                          </p>
                          <p className="truncate text-xs text-gray-500">
                            {organizationDisplayName(row.quote?.organization)}
                            {' · '}demandé le {formatNr2jDate(row.requestedAt)}
                          </p>
                        </div>
                        <span className="shrink-0 text-xs font-medium text-indigo-700">Examiner</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Section
              title="Tâches ouvertes"
              action={(
                <button type="button" onClick={() => setTab('tasks')} className="text-xs font-medium text-indigo-700">
                  Toutes
                </button>
              )}
            >
              <TasksList items={openTasks} emptyText="Aucune tâche ouverte." />
            </Section>

            <Section
              title="Prospects sans activité récente"
              action={(
                <button type="button" onClick={() => setTab('prospects')} className="text-xs font-medium text-indigo-700">
                  Tous les prospects
                </button>
              )}
            >
              {staleProspects.length === 0 ? (
                <EmptyRow text="Aucun prospect dormant." />
              ) : (
                <ul className="space-y-2">
                  {staleProspects.map((org) => (
                    <li key={org.id}>
                      <Link
                        to={`/nr2j/clients/${org.id}`}
                        className="flex items-center justify-between gap-3 rounded-lg px-3 py-2 hover:bg-gray-50"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">{organizationDisplayName(org)}</p>
                          <p className="truncate text-xs text-gray-500">
                            {nr2jStageLabel(org.pipelineStage)}
                            {' · '}mis à jour le {formatNr2jDate(org.updatedAt)}
                          </p>
                        </div>
                        <ArrowRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>

          <Section
            title="Derniers devis"
            action={(
              <button type="button" onClick={() => setTab('quotes')} className="text-xs font-medium text-indigo-700">
                Tous les devis
              </button>
            )}
          >
            <QuotesTable items={recentQuotes} emptyText="Aucun devis." />
          </Section>

          <Section
            title="Activité récente"
            action={(
              <button type="button" onClick={() => setTab('activity')} className="text-xs font-medium text-indigo-700">
                Toute l’activité
              </button>
            )}
          >
            <ActivityList items={recentActivity} emptyText="Aucun événement." memberId={member.id} />
          </Section>
        </div>
      ) : (
        <Section
          title={TABS.find((t) => t.id === tab)?.label}
          testId={`nr2j-my-team-detail-${tab}`}
          action={listLinks[tab] ? (
            <Link to={listLinks[tab].to} className="text-xs font-medium text-indigo-700">
              {listLinks[tab].label}
            </Link>
          ) : null}
        >
          {sectionPending ? (
            <p className="text-sm text-gray-500">Chargement…</p>
          ) : tab === 'prospects' ? (
            <OrganizationsTable items={sectionItems} emptyText="Aucun prospect." />
          ) : tab === 'clients' ? (
            <OrganizationsTable items={sectionItems} emptyText="Aucun client NR2J." />
          ) : tab === 'tasks' ? (
            <TasksList items={sectionItems} emptyText="Aucune tâche." />
          ) : tab === 'quotes' ? (
            <QuotesTable items={sectionItems} emptyText="Aucun devis." />
          ) : (
            <ActivityList items={sectionItems} emptyText="Aucun événement." memberId={member.id} />
          )}
        </Section>
      )}

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </div>
  );
}
